import type { FilterOption } from '@/components/kit/filters'
import type { ICard } from '@/types/card'

export const MANUAL_HIDE_PREFIX = '[manual] '

/**
 * The backend marks an admin-driven hide by prefixing the reason. Distinguishing it
 * from a sync-driven hide matters: a manual hide is a decision someone made and can
 * be undone here, whereas a sync hide comes back on the next price run.
 */
export function isManuallyHidden(reason?: string | null): boolean {
    return Boolean(reason) && reason!.startsWith(MANUAL_HIDE_PREFIX)
}

export function displayHideReason(reason?: string | null): string {
    if (!reason) return ''
    return isManuallyHidden(reason) ? reason.slice(MANUAL_HIDE_PREFIX.length) : reason
}

export const GAME_OPTIONS: FilterOption<string>[] = [
    { value: 'pokemon', label: 'Pok\u00e9mon' },
    { value: 'mtg', label: 'Magic: The Gathering' },
    { value: 'one-piece-card-game', label: 'One Piece' },
    { value: 'riftbound-league-of-legends-trading-card-game', label: 'Riftbound' }
]

export const LANGUAGE_OPTIONS: FilterOption<string>[] = [
    { value: 'English', label: 'English' },
    { value: 'Japanese', label: 'Japanese' },
    { value: 'French', label: 'French' },
    { value: 'German', label: 'German' },
    { value: 'Italian', label: 'Italian' },
    { value: 'Spanish', label: 'Spanish' },
    { value: 'Portuguese', label: 'Portuguese' }
]

export const SINGLE_CONDITIONS = [
    'Near Mint',
    'Lightly Played',
    'Moderately Played',
    'Heavily Played',
    'Damaged'
] as const

export const CONDITION_OPTIONS: FilterOption<string>[] = SINGLE_CONDITIONS.map(value => ({ value, label: value }))

export const PRINTING_OPTIONS: FilterOption<string>[] = [
    'Normal',
    'Holofoil',
    'Reverse Holofoil',
    'Foil',
    '1st Edition',
    '1st Edition Holofoil',
    'Unlimited',
    'Unlimited Holofoil'
].map(value => ({ value, label: value }))

export const CARD_TYPE_OPTIONS: FilterOption<'all' | 'singles' | 'sealed'>[] = [
    { value: 'all', label: 'All items' },
    { value: 'singles', label: 'Singles only' },
    { value: 'sealed', label: 'Sealed only', hint: 'Boxes and packs, which have condition "Sealed"' }
]

/**
 * Only what `/cards/search` will actually accept. The legacy UI also offered Set and
 * Number, which the endpoint's schema rejects -- picking either returned a 400 that
 * the page logged to the console and otherwise swallowed, so the grid just stopped
 * updating.
 */
export const CARD_SORT_OPTIONS: FilterOption<'' | 'price' | 'name' | 'createdAt'>[] = [
    { value: '', label: 'Relevance' },
    { value: 'price', label: 'Price' },
    { value: 'name', label: 'Name' },
    { value: 'createdAt', label: 'Date added' }
]

const PRINTING_SUFFIX: Record<string, string> = {
    Normal: '',
    Holofoil: '@HF',
    'Reverse Holofoil': '@RHF',
    Foil: '@F',
    '1st Edition': '@1E',
    '1st Edition Holofoil': '@1EHF',
    Unlimited: '@U',
    'Unlimited Holofoil': '@UHF'
}

const CONDITION_SUFFIX: Record<string, string> = {
    'Near Mint': '',
    'Lightly Played': '@LP',
    'Moderately Played': '@MP',
    'Heavily Played': '@HP',
    Damaged: '@DMG'
}

/**
 * The identity of a card *listing*, not of the card record: the same `_id` comes back
 * once per printing and condition, so keying a grid on `_id` alone collapses rows the
 * operator needs to tell apart.
 */
export function cardKey(card: ICard): string {
    const printing = PRINTING_SUFFIX[card.price?.printing ?? ''] ?? ''
    const condition = CONDITION_SUFFIX[card.price?.condition ?? ''] ?? ''
    return (card.justTcgId ?? card._id) + printing + condition
}

export function isSealed(card: ICard): boolean {
    return card.price?.condition === 'Sealed'
}

export function cardImage(card: ICard): string | undefined {
    return card.image_custom || card.image
}

export function cardSubtitle(card: ICard): string {
    return [card.set, card.number && `#${card.number}`, card.rarity].filter(Boolean).join(' \u00b7 ')
}

export function gameLabel(game: string): string {
    return GAME_OPTIONS.find(option => option.value === game)?.label ?? game
}
